/* ============================================================================
   PANEL DE METAR

   Va en las secciones de despegue y aterrizaje, junto a la temperatura.

   POR QUÉ EL TEXTO CRUDO, TAL CUAL

   La temperatura que ya estaba ahí sale de Open-Meteo, que es un **pronóstico
   de modelo**. El METAR es una **observación**: alguien midió eso, en ese
   aeródromo, a esa hora. Para un registro de planificación auditable —BARS— no
   sirve un resumen ni una interpretación: tiene que estar el informe textual,
   con su hora de emisión, tal como lo publicó el servicio.

   Por eso el crudo se muestra completo y en tipografía monoespaciada, y se
   puede copiar. Lo demás —la lectura y la clasificación— va debajo, como
   ayuda, nunca en lugar del original.

   REGLA QUE NO SE NEGOCIA

   El texto crudo **nunca se edita, se recorta ni se normaliza** para que quepa
   mejor. Un informe alterado en un registro de auditoría es peor que no
   tenerlo.
   ========================================================================== */

import React from "react";

import { analizarMetar, INDICE_FRAT } from "../../domain/metar.js";

/** Colores por categoría del FRAT. La precaución de esta app es amarilla. */
const TONO = {
  CAVOK: { borde: "#4ADE80", texto: "#4ADE80", etiqueta: "CAVOK" },
  "1500-5KM": { borde: "#FFD23F", texto: "#FFD23F", etiqueta: "1500' – 5 KM" },
  "SIN-INFO": { borde: "#E4573D", texto: "#E4573D", etiqueta: "Sin información" },
};

/**
 * @param {object} props
 * @param {string|null} props.oaci      código del aeródromo
 * @param {string|null} props.crudo     el METAR textual, tal como llegó
 * @param {string|null} props.motivoFalla por qué no hay informe
 * @param {boolean} props.cargando
 * @param {Function} props.alActualizar
 * @param {Date} [props.ahora]          para poder probarlo con una hora fija
 */
export default function PanelMetar({
  oaci = null,
  crudo = null,
  motivoFalla = null,
  cargando = false,
  alActualizar = null,
  ahora = undefined,
}) {
  const analisis = React.useMemo(
    () => (crudo ? analizarMetar(crudo, ahora || new Date()) : null),
    [crudo, ahora],
  );

  /* Sin código OACI no hay METAR que pedir. Es el caso normal cuando el punto
     es una coordenada suelta y no un aeródromo, así que se dice sin alarma. */
  if (!oaci) {
    return (
      <div className="hc-metar hc-metar--vacio">
        <span className="hc-metar__titulo">METAR</span>
        <span className="hc-metar__nota">
          Este punto no es un aeródromo con código OACI. El METAR solo existe para aeródromos.
        </span>
      </div>
    );
  }

  const tono = analisis ? TONO[analisis.categoria] : TONO["SIN-INFO"];

  return (
    <div className="hc-metar" style={{ borderColor: tono.borde }}>
      <div className="hc-metar__cabecera">
        <span className="hc-metar__titulo">METAR {oaci}</span>
        {analisis?.horaUTC && (
          <span className="hc-metar__hora">
            {analisis.esSpeci ? "SPECI " : ""}{analisis.horaUTC} UTC
            {Number.isFinite(analisis.antiguedadMin) && ` · hace ${analisis.antiguedadMin} min`}
          </span>
        )}
        {alActualizar && (
          <button type="button" className="hc-metar__boton" onClick={alActualizar} disabled={cargando}>
            {cargando ? "Consultando…" : "Actualizar"}
          </button>
        )}
      </div>

      {/* EL INFORME TEXTUAL, VERBATIM. Es lo que queda en el registro de
          planificación y lo que mira un auditor. Nunca se recorta. */}
      {crudo && (
        <pre className="hc-metar__crudo" title="Informe textual, tal como lo publicó el servicio">
          {crudo}
        </pre>
      )}

      {!crudo && (
        <p className="hc-metar__nota">
          {motivoFalla || (cargando ? "Consultando el informe…" : "Sin informe todavía.")}
        </p>
      )}

      {analisis && (
        <>
          <div className="hc-metar__frat" style={{ color: tono.texto }}>
            <strong>FRAT meteorología: {tono.etiqueta}</strong>
            <span className="hc-metar__motivo">{analisis.motivo}</span>
          </div>

          {/* La lectura desglosada va DEBAJO del crudo y es ayuda, no reemplazo.
              Un dato que no se pudo leer va con guion, nunca con cero. */}
          <div className="hc-metar__lectura">
            <Dato rotulo="Viento" valor={textoViento(analisis.viento)} />
            <Dato rotulo="Visibilidad" valor={textoVisibilidad(analisis.visibilidadM)} />
            <Dato rotulo="Techo" valor={textoTecho(analisis.techoFt)} />
            <Dato rotulo="Temp / rocío" valor={textoTemp(analisis)} />
            <Dato rotulo="QNH" valor={analisis.qnhHpa === null ? null : `${analisis.qnhHpa} hPa`} />
          </div>

          {analisis.fenomenos.length > 0 && (
            <p className="hc-metar__fenomenos">
              Fenómeno significativo: {analisis.fenomenos.join(", ")}.
            </p>
          )}

          {analisis.avisos.map((a) => (
            <p key={a} className="hc-metar__aviso">{a}</p>
          ))}
        </>
      )}

      {/* Aclaración permanente. La aplicación no reemplaza a la fuente oficial,
          y en un registro de auditoría eso tiene que estar dicho. */}
      <p className="hc-metar__origen">
        Observación de aviationweather.gov. No reemplaza a la consulta oficial ante la autoridad.
      </p>
    </div>
  );
}

function Dato({ rotulo, valor }) {
  return (
    <span className="hc-metar__dato">
      <span className="hc-metar__dato-rotulo">{rotulo}</span>
      <span className="hc-metar__dato-valor">{valor ?? "—"}</span>
    </span>
  );
}

function textoViento(v) {
  if (!v || v.nudos === null) return null;
  if (v.nudos === 0) return "calma";
  const dir = v.variable ? "variable" : `del ${String(v.direccionGrados).padStart(3, "0")}°`;
  return `${v.nudos} kt ${dir}${v.rafagaKt ? ` · ráfagas ${v.rafagaKt} kt` : ""}`;
}

function textoVisibilidad(m) {
  if (m === null) return null;
  return m >= 10000 ? "10 km o más" : `${m.toLocaleString("es")} m`;
}

function textoTecho(ft) {
  if (ft === null) return null;
  return ft >= 99999 ? "sin nubes bajas" : `${ft.toLocaleString("es")} ft`;
}

function textoTemp(a) {
  if (a.temperaturaC === null) return null;
  return `${a.temperaturaC} / ${a.rocioC ?? "—"} °C`;
}

export { INDICE_FRAT };
